import React, { useState, useEffect } from 'react';
import { Card, Row, Col, ProgressBar, Spinner, Alert } from 'react-bootstrap';
import { FaCheckCircle, FaTimesCircle, FaCalendarAlt } from 'react-icons/fa';
import { getAttendanceSummary } from '../../services/firebase/attendanceService';

const AttendanceSummarySection = ({ employeeId }) => {
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth() + 1);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());

  useEffect(() => {
    const fetchSummary = async () => {
      if (!employeeId) return;

      setLoading(true);
      try {
        const month = selectedMonth;
        const year = selectedYear;

        console.log('[AttendanceSummarySection] Fetching for employeeId:', employeeId, 'month:', month, 'year:', year);
        const response = await getAttendanceSummary(employeeId, month, year);
        console.log('[AttendanceSummarySection] Response:', response);
        
        // Handle response structure: { success: true, data: {...} }
        const summaryData = response?.data || response;
        
        // Normalize field names
        const normalizedSummary = {
          present: summaryData?.presentDays || summaryData?.present || 0,
          absent: summaryData?.absentDays || summaryData?.absent || 0,
          leave: summaryData?.leaveDays || summaryData?.leave || 0,
          halfDays: summaryData?.halfDays || 0,
          totalDays: summaryData?.totalDays || 0,
          percentage: summaryData?.attendancePercentage || summaryData?.percentage || 0
        };
        
        console.log('[AttendanceSummarySection] Normalized summary:', normalizedSummary);
        setSummary(normalizedSummary);
      } catch (err) {
        console.error('Error fetching attendance summary:', err);
        setError('Failed to load attendance summary');
      } finally {
        setLoading(false);
      }
    };

    fetchSummary();
  }, [employeeId, selectedMonth, selectedYear]);

  if (loading) {
    return (
      <Card className="text-center p-4">
        <Spinner animation="border" />
      </Card>
    );
  }

  if (error) {
    return (
      <Card>
        <Card.Body>
          <Alert variant="danger">{error}</Alert>
        </Card.Body>
      </Card>
    );
  }

  if (!summary) return null;

  // Calculate percentage if not provided by backend
  const percentage = summary.percentage || (summary.totalDays > 0 ? Math.round((summary.present / summary.totalDays) * 100) : 0);

  const monthNames = ['January', 'February', 'March', 'April', 'May', 'June', 
                       'July', 'August', 'September', 'October', 'November', 'December'];

  return (
    <Card>
      <Card.Header className="d-flex justify-content-between align-items-center flex-wrap gap-2 py-2 py-md-3">
        <h6 className="mb-0 fw-bold">Attendance Summary</h6>
        <div className="d-flex gap-2">
          <select 
            className="form-select form-select-sm" 
            style={{ width: 'auto' }}
            value={selectedMonth}
            onChange={(e) => setSelectedMonth(parseInt(e.target.value))}
          >
            {monthNames.map((name, index) => (
              <option key={index + 1} value={index + 1}>{name}</option>
            ))}
          </select>
          <select 
            className="form-select form-select-sm" 
            style={{ width: 'auto' }}
            value={selectedYear}
            onChange={(e) => setSelectedYear(parseInt(e.target.value))}
          >
            {[2024, 2025, 2026].map(y => (
              <option key={y} value={y}>{y}</option>
            ))}
          </select>
        </div>
      </Card.Header>
      <Card.Body className="p-2 p-md-3">
        <Row className="g-2 g-md-3 mb-3 mb-md-4">
          <Col xs={4} md={4} className="text-center">
            <div className="p-2 p-md-3 rounded" style={{ backgroundColor: 'rgba(25, 135, 84, 0.1)' }}>
              <FaCheckCircle className="text-success" size={24} />
              <h4 className="mt-1 mt-md-2 mb-0 fs-5 fs-md-3 fw-bold" style={{ color: '#000000' }}>{summary.present || 0}</h4>
              <small className="d-block text-truncate" style={{ color: '#000000', fontSize: '0.72rem' }}>Present</small>
            </div>
          </Col>
          <Col xs={4} md={4} className="text-center">
            <div className="p-2 p-md-3 rounded" style={{ backgroundColor: 'rgba(220, 53, 69, 0.1)' }}>
              <FaTimesCircle className="text-danger" size={24} />
              <h4 className="mt-1 mt-md-2 mb-0 fs-5 fs-md-3 fw-bold" style={{ color: '#000000' }}>{summary.absent || 0}</h4>
              <small className="d-block text-truncate" style={{ color: '#000000', fontSize: '0.72rem' }}>Absent</small>
            </div>
          </Col>
          <Col xs={4} md={4} className="text-center">
            <div className="p-2 p-md-3 rounded" style={{ backgroundColor: 'rgba(255, 193, 7, 0.1)' }}>
              <FaCalendarAlt className="text-warning" size={24} />
              <h4 className="mt-1 mt-md-2 mb-0 fs-5 fs-md-3 fw-bold" style={{ color: '#000000' }}>{summary.leave || 0}</h4>
              <small className="d-block text-truncate" style={{ color: '#000000', fontSize: '0.72rem' }}>Leave</small>
            </div>
          </Col>
        </Row>

        <div className="mb-3">
          <div className="d-flex justify-content-between mb-2">
            <span>Attendance Percentage</span>
            <span className="fw-bold">{percentage}%</span>
          </div>
          <ProgressBar
            now={percentage}
            variant={percentage >= 90 ? 'success' : percentage >= 75 ? 'warning' : 'danger'}
          />
        </div>

        <div className="text-center mt-4">
          <small className="text-muted">
            Total Working Days: {summary.totalDays || 0}
          </small>
        </div>
      </Card.Body>
    </Card>
  );
};

export default AttendanceSummarySection;
